import { _isNil, _notNil } from '@/littledash';
import type {
  SendIncompleteReason,
  SendMissingInputs,
  SendReportCounts,
  SendExactPass,
  SendResultReport,
  SendRuleOutcome,
  SendVerdictTally,
} from '@/model/Agent.model';

/** How each reason a check did not complete, or a rule did not apply, reads to a reviewer. */
export const REASON_TEXT: Record<SendIncompleteReason, string> = {
  binding_unresolved:
    'What a value is checked against could not be determined: its codelist, or a dataset uploaded more than once',
  cardinality_limit_reached: 'Stopped at a size limit before every record was compared',
  dataset_not_uploaded: 'Not applicable to this upload: no dataset it applies to was uploaded',
  evaluation_error: 'The check could not be run',
  implementation_absent: 'Not checked by this version of the validator yet',
  input_unavailable_in_profile: 'Needs information a CSV upload does not carry',
  insufficient_precision: 'A date is recorded too imprecisely to compare',
  not_determinable: 'No validator can check this from a submission',
  out_of_scope_version: 'The declared SEND version is not one this validator checks',
  required_domain_missing: 'A dataset the check needs was not uploaded',
  scope_unknown: 'The SEND version could not be determined',
  scope_use_case: "Doesn't apply to SEND",
  value_uninterpretable: 'A value could not be read the way the check needs',
  variable_absent: 'A column the rule reads is not in the file',
  variable_not_defined_for_domain: 'Not applicable: SENDIG defines no such variable for this dataset',
};

/**
 * Why a rule that ran was left partly unchecked, as its partial result's reasons read to a reviewer.
 * Worded for the part left out: the rest of the rule was checked, and may have passed or failed.
 */
const PARTIAL_REASON_TEXT: Partial<Record<SendIncompleteReason, string>> = {
  binding_unresolved:
    'For some values, what to check against could not be determined: a codelist, or a dataset uploaded more than once',
  cardinality_limit_reached: 'A file was too large to check in full',
  implementation_absent: 'Part of the rule is not checked by this version of the validator yet',
  insufficient_precision: 'Some dates are recorded too imprecisely to compare',
  required_domain_missing: 'A dataset part of the rule needs was not uploaded',
  value_uninterpretable: 'Some values could not be read the way the rule needs',
  variable_absent: 'A file is missing a column the rule reads, so that file was not checked',
};

/** How a check covering only part of a rule is labelled, on the rule and on its findings. */
export const PARTIAL_CHECK = 'Partial check';

/** A rule the upload was checked against only in part, and why. */
export interface SendPartialRule {
  ruleId: string;
  reason: string;
}

/**
 * Whether a rule's check covers only part of the rule. A check that ran and did not finish is listed
 * apart, under the rules that did not finish, so neither list repeats the other.
 */
const isCheckedPartially = (outcome: SendRuleOutcome): boolean => outcome.implementation === 'proxy';

/**
 * Why a rule was checked only in part: the reason the server gives for the rule where it has one,
 * otherwise what stopped this run.
 */
const partialReasonsText = (reasons: ReadonlyArray<SendIncompleteReason>): string =>
  reasons.map((reason) => PARTIAL_REASON_TEXT[reason] ?? REASON_TEXT[reason] ?? reason).join('; ');

const partialReasonOf = (outcome: SendRuleOutcome): string =>
  _notNil(outcome.reason) ? outcome.reason : partialReasonsText(outcome.result.reasons);

/** The published rules whose check covers only part of the rule, in the server's order. */
export const partialRulesOf = (report: SendResultReport): Array<SendPartialRule> =>
  report.rule_outcomes.reduce<Array<SendPartialRule>>((partial, outcome) => {
    if (isCheckedPartially(outcome)) {
      partial.push({ ruleId: outcome.rule_id, reason: partialReasonOf(outcome) });
    }
    return partial;
  }, []);

/**
 * The rules whose check covers only part of the rule, published or not, so their findings can carry
 * the same label.
 */
export const proxyRulesOf = (report: SendResultReport): ReadonlySet<string> =>
  [...report.rule_outcomes, ...report.encoding_outcomes].reduce<Set<string>>((proxies, outcome) => {
    if (outcome.implementation === 'proxy') {
      proxies.add(outcome.rule_id);
    }
    return proxies;
  }, new Set());

/** What the rules made of one file, read from the server's tally and verdict for its dataset. */
export interface SendFileRuleCheck {
  /** The server's verdict that the dataset passed validation exactly, for a study in scope; never worked out here. */
  exactPass: boolean;
  /** Rules that failed for the file, whether or not its own findings carry them. */
  failed: number;
  notEvaluated: number;
  /** Rules that passed without an exact, finished check. */
  checkedPartially: number;
  /** The file's checks that said they could not decide something. */
  undecided: number;
}

/**
 * The file's rule check: whether it passed exactly, as the server decided for its dataset (gated on scope
 * by `exactPassOf`), beside the dataset's row of the server's tally and the file's exact counts. Null for
 * a file no rule reached, which has no row in the grid.
 */
export const fileRuleCheckOf = (
  report: SendResultReport,
  datasetName: string,
  counts: SendReportCounts,
  exactPass: SendExactPass | undefined
): SendFileRuleCheck | null => {
  const tally: SendVerdictTally | undefined = report.coverage_grid.by_domain.find(
    (row) => row.key === datasetName
  )?.tally;
  const undecided = counts.undecided_checks;
  if (_isNil(tally)) {
    return null;
  }
  return {
    exactPass: exactPass?.by_dataset.find((dataset) => dataset.key === datasetName)?.exact_pass ?? false,
    failed: tally.failed,
    notEvaluated: tally.not_evaluated,
    checkedPartially: tally.passed - tally.conforming,
    undecided,
  };
};

/** The reasons that make a rule not apply to an upload, as the server reads them, rather than leave it unchecked. */
const INAPPLICABILITY_REASONS: ReadonlySet<SendIncompleteReason> = new Set([
  'dataset_not_uploaded',
  'scope_use_case',
  'variable_not_defined_for_domain',
]);

/** One published rule the run did not settle, with what the report can say about it. */
export interface SendUnsettledRule {
  ruleId: string;
  reasons: Array<SendIncompleteReason>;
  /** The server's sentence on why this profile can't fully answer the rule, if it gives one. */
  prose: string | null;
  /** No check exists for the rule yet, so any prose describes a check still to be built. */
  notBuilt: boolean;
  /** For a check that ran and did not finish: whether it had already found a problem. */
  foundProblems: boolean;
  /** The columns and datasets the server says the upload lacked for this rule, if it names any. */
  missingInputs: SendMissingInputs | null;
}

/** Rules sharing one reason, under that reason in plain words. */
export interface SendUnsettledGroup {
  key: string;
  title: string;
  rules: Array<SendUnsettledRule>;
}

/** Every published rule the run did not settle, by why, plus those that did not apply. */
export interface SendUnsettledRules {
  /** Rules no check ran for, one group per reason that left them unchecked. */
  notEvaluated: Array<SendUnsettledGroup>;
  /** Checks that started and stopped before deciding anything. Counted as not evaluated. */
  didNotFinish: Array<SendUnsettledRule>;
  /**
   * Checks that decided pass or fail on part of what they read, then stopped. The server counts
   * them as passed or failed, so they are not among the rules not evaluated.
   */
  decidedInPart: Array<SendUnsettledRule>;
  /** Checks that failed to run: a defect in the validator, not a decision about the data. */
  evaluationErrors: Array<SendUnsettledRule>;
  /** Rules that do not apply to this upload, one group per reason. Not a gap. */
  notApplicable: Array<SendUnsettledGroup>;
}

const unsettledRuleOf = (outcome: SendRuleOutcome): SendUnsettledRule => ({
  ruleId: outcome.rule_id,
  reasons: outcome.result.reasons,
  prose: outcome.reason,
  notBuilt: outcome.result.reasons.includes('implementation_absent'),
  foundProblems: outcome.result.verdict === 'fail',
  missingInputs: outcome.missing_inputs ?? null,
});

const addToGroup = (groups: Map<string, SendUnsettledGroup>, reason: SendIncompleteReason, rule: SendUnsettledRule) => {
  const group = groups.get(reason) ?? { key: reason, title: REASON_TEXT[reason] ?? reason, rules: [] };
  group.rules.push(rule);
  groups.set(reason, group);
};

const isInapplicable = (reasons: ReadonlyArray<SendIncompleteReason>): boolean =>
  reasons.length > 0 && reasons.every((reason) => INAPPLICABILITY_REASONS.has(reason));

/**
 * Sorts the published rules the run did not settle into what the report shows of each, bucketing
 * them as the server's tally does: on the verdict first, so a check that decided pass or fail on
 * part of what it read is counted with the passes or failures, never as not evaluated. The rest are
 * not applicable, failed to run, started and stopped before deciding, or not evaluated, each with
 * why. The headings carry the tally's numbers rather than a count of these lists.
 */
export const unsettledRulesOf = (report: SendResultReport): SendUnsettledRules => {
  const notEvaluated = new Map<string, SendUnsettledGroup>();
  const notApplicable = new Map<string, SendUnsettledGroup>();
  const didNotFinish: Array<SendUnsettledRule> = [];
  const decidedInPart: Array<SendUnsettledRule> = [];
  const evaluationErrors: Array<SendUnsettledRule> = [];
  report.rule_outcomes.forEach((outcome) => {
    const { verdict, evaluation_status: status, reasons } = outcome.result;
    const rule = unsettledRuleOf(outcome);
    if (verdict !== 'unknown') {
      if (status !== 'complete') {
        decidedInPart.push(rule);
      }
    } else if (isInapplicable(reasons)) {
      addToGroup(notApplicable, reasons[0], rule);
    } else if (status === 'error') {
      evaluationErrors.push(rule);
    } else if (status === 'partial') {
      didNotFinish.push(rule);
    } else if (reasons.length > 0) {
      // Reasons arrive in token order, so the first can be an inapplicability one even when another
      // means the rule still applies: group by the first that does.
      addToGroup(notEvaluated, reasons.find((reason) => !INAPPLICABILITY_REASONS.has(reason)) ?? reasons[0], rule);
    }
  });
  return {
    notEvaluated: [...notEvaluated.values()],
    didNotFinish,
    decidedInPart,
    evaluationErrors,
    notApplicable: [...notApplicable.values()],
  };
};

/** Why a check that ran and did not finish left part of its rule unchecked, as a reviewer reads it. */
export const unfinishedReasonOf = (rule: SendUnsettledRule): string => partialReasonsText(rule.reasons);

/**
 * What the upload lacked for a rule, in a line a reviewer can act on: the datasets to upload, then the columns
 * the rule needs, then how many more the server left off the list. For a rule that does not apply, the datasets
 * are the ones it is about, never ones to upload. Null when the server names nothing.
 */
export const missingInputsText = (rule: SendUnsettledRule): string | null => {
  const missing = rule.missingInputs;
  if (_isNil(missing)) {
    return null;
  }
  const parts: Array<string> = [];
  if (missing.datasets.length > 0) {
    parts.push(`${isInapplicable(rule.reasons) ? 'Applies to' : 'Upload'} ${missing.datasets.join(', ')}`);
  }
  if (missing.columns.length > 0) {
    parts.push(`Needs ${missing.columns.join(', ')}`);
  }
  if (parts.length === 0) {
    return `${missing.omitted} missing, not named`;
  }
  const text = parts.join(' · ');
  return missing.omitted > 0 ? `${text}, … and ${missing.omitted} more` : text;
};

/** Whether the run left any published rule unsettled, so there is anything to list. */
export const hasUnsettledRules = (unsettled: SendUnsettledRules): boolean =>
  unsettled.notEvaluated.length > 0 ||
  unsettled.didNotFinish.length > 0 ||
  unsettled.decidedInPart.length > 0 ||
  unsettled.evaluationErrors.length > 0 ||
  unsettled.notApplicable.length > 0;
